import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EnergyType,
  TelesonTariff,
  ContractTemplate,
  ContractFieldValue,
  CustomerType,
  STGender,
  EnergyTypeFormState,
  STOfferFormState,
} from '@/types';

// Initial state for a single energy type section
const initialEnergyTypeState: EnergyTypeFormState = {
  included: false,
  currentProvider: '',
  currentBasePrice: 0,
  currentWorkingPrice: 0,
  yearlyUsage: 0,
  meterNumber: '',
  contractNumber: '',
  selectedTariffId: null,
  selectedTariff: null,
  includeContract: false,
  availableTariffs: [],
  isLoadingTariffs: false,
  // Contract template data
  contractTemplate: null,
  contractFields: {},
  isLoadingContract: false,
};

// Initial form state
const initialState: STOfferFormState = {
  // Step tracking
  currentStep: 0,
  isOpen: false,
  isSubmitting: false,

  // Lead context
  leadId: null,
  leadName: '',

  // Customer type
  customerType: 'Gewerbe',

  // Customer data
  gender: null,
  title: '',
  firstname: '',
  lastname: '',
  birthday: null,
  phone: '',
  companyName: '',
  zip: '',
  city: '',
  streetName: '',
  streetNumber: '',
  email: '',
  iban: '',
  bic: '',
  bankName: '',
  isNewOccupant: false,

  // Agent assignment (Admin only)
  assignedUserId: null,

  // Energy types
  gas: { ...initialEnergyTypeState },
  electricity: { ...initialEnergyTypeState },
  heating: { ...initialEnergyTypeState },

  // Appointment
  scheduleAppointment: false,
  appointmentDate: null,
  closeMyself: true,
  selectedCloser: null,
  appointmentDuration: 30,
  appointmentIsOnline: false,
  appointmentMeetingLink: '',
  appointmentLocation: 'Telefon',
  appointmentNotes: '',

  // Contract templates
  availableTemplates: [],
  selectedTemplateIds: {
    GAS: null,
    ELECTRICITY: null,
    HEATING: null,
  },
  contractFieldValues: {},

  // Contact
  selectedContactId: null,
  contactSyncMode: 'none',
  isCreatingNewContact: false,
  newContactData: null,

  // Lead addresses
  leadAddresses: [],

  // Errors
  errors: {},
};

export const stOfferFormSlice = createSlice({
  name: 'stOfferForm',
  initialState,
  reducers: {
    // Open/Close form
    openForm: (
      state,
      action: PayloadAction<{ leadId: number; leadName: string; prefillData?: Partial<STOfferFormState> }>
    ) => {
      // If same lead already has data, just reopen (resume)
      if (state.leadId === action.payload.leadId && state.leadId !== null) {
        state.isOpen = true;
        return;
      }

      // Different lead or no existing data: reset and prefill
      Object.assign(state, initialState);

      // Set lead context
      state.isOpen = true;
      state.leadId = action.payload.leadId;
      state.leadName = action.payload.leadName;

      // Apply prefill data if provided
      if (action.payload.prefillData) {
        Object.assign(state, action.payload.prefillData);
      }
    },

    closeForm: (state) => {
      state.isOpen = false;
    },

    resetForm: () => initialState,

    // Step navigation
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    nextStep: (state) => {
      state.currentStep += 1;
    },

    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    // Customer type
    setCustomerType: (state, action: PayloadAction<CustomerType>) => {
      state.customerType = action.payload;
    },

    // Customer data
    setGender: (state, action: PayloadAction<STGender | null>) => {
      state.gender = action.payload;
    },

    setCustomerData: (
      state,
      action: PayloadAction<{
        title?: string;
        firstname?: string;
        lastname?: string;
        birthday?: string | null;
        phone?: string;
        companyName?: string;
        zip?: string;
        city?: string;
        streetName?: string;
        streetNumber?: string;
        email?: string;
      }>
    ) => {
      Object.assign(state, action.payload);
    },

    // Bank data
    setBankData: (
      state,
      action: PayloadAction<{
        iban?: string;
        bic?: string;
        bankName?: string;
      }>
    ) => {
      Object.assign(state, action.payload);
    },

    setIsNewOccupant: (state, action: PayloadAction<boolean>) => {
      state.isNewOccupant = action.payload;
    },

    // Agent assignment (Admin only)
    setAssignedUserId: (state, action: PayloadAction<number | null>) => {
      state.assignedUserId = action.payload;
    },

    // Energy type inclusion toggle
    toggleEnergyType: (state, action: PayloadAction<EnergyType>) => {
      const energyKey = action.payload.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].included = !state[energyKey].included;

      // Reset tariff selection when toggling off
      if (!state[energyKey].included) {
        state[energyKey].selectedTariffId = null;
        state[energyKey].selectedTariff = null;
        state[energyKey].availableTariffs = [];
      }
    },

    // Energy type data
    setEnergyTypeData: (
      state,
      action: PayloadAction<{
        energyType: EnergyType;
        data: Partial<EnergyTypeFormState>;
      }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      Object.assign(state[energyKey], action.payload.data);
    },

    // Tariffs
    setTariffsLoading: (
      state,
      action: PayloadAction<{ energyType: EnergyType; loading: boolean }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].isLoadingTariffs = action.payload.loading;
    },

    setAvailableTariffs: (
      state,
      action: PayloadAction<{ energyType: EnergyType; tariffs: TelesonTariff[] }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].availableTariffs = action.payload.tariffs;
      state[energyKey].isLoadingTariffs = false;
    },

    selectTariff: (
      state,
      action: PayloadAction<{ energyType: EnergyType; tariff: TelesonTariff | null }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].selectedTariff = action.payload.tariff;
      state[energyKey].selectedTariffId = action.payload.tariff?.product_tariff_id.toString() || null;
    },

    // Contract
    setIncludeContract: (
      state,
      action: PayloadAction<{ energyType: EnergyType; include: boolean }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].includeContract = action.payload.include;
      // Clear contract data when unchecking
      if (!action.payload.include) {
        state[energyKey].contractTemplate = null;
        state[energyKey].contractFields = {};
      }
    },

    setContractLoading: (
      state,
      action: PayloadAction<{ energyType: EnergyType; loading: boolean }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].isLoadingContract = action.payload.loading;
    },

    setContractTemplate: (
      state,
      action: PayloadAction<{
        energyType: EnergyType;
        template: ContractTemplate | null;
        fields: Record<string, ContractFieldValue>;
      }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      state[energyKey].contractTemplate = action.payload.template;
      state[energyKey].contractFields = action.payload.fields;
      state[energyKey].isLoadingContract = false;
    },

    updateContractField: (
      state,
      action: PayloadAction<{
        energyType: EnergyType;
        fieldUuid: string;
        value: string;
      }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      if (state[energyKey].contractFields[action.payload.fieldUuid]) {
        state[energyKey].contractFields[action.payload.fieldUuid].value = action.payload.value;
      }
    },

    // Update field visibility based on conditions
    updateFieldVisibility: (
      state,
      action: PayloadAction<{
        energyType: EnergyType;
        visibilityMap: Record<string, boolean>;
      }>
    ) => {
      const energyKey = action.payload.energyType.toLowerCase() as 'gas' | 'electricity' | 'heating';
      const fields = state[energyKey].contractFields;
      for (const [uuid, isVisible] of Object.entries(action.payload.visibilityMap)) {
        if (fields[uuid]) {
          fields[uuid].isVisible = isVisible;
        }
      }
    },

    setAvailableTemplates: (state, action: PayloadAction<ContractTemplate[]>) => {
      state.availableTemplates = action.payload;
    },

    setSelectedTemplate: (
      state,
      action: PayloadAction<{ energyType: EnergyType; templateId: number | null }>
    ) => {
      state.selectedTemplateIds[action.payload.energyType] = action.payload.templateId;
    },

    setContractFieldValue: (
      state,
      action: PayloadAction<{ fieldUuid: string; value: string }>
    ) => {
      state.contractFieldValues[action.payload.fieldUuid] = action.payload.value;
    },

    // Appointment
    setScheduleAppointment: (state, action: PayloadAction<boolean>) => {
      state.scheduleAppointment = action.payload;
    },

    setAppointmentDate: (state, action: PayloadAction<string | null>) => {
      state.appointmentDate = action.payload;
    },

    setCloseMyself: (state, action: PayloadAction<boolean>) => {
      state.closeMyself = action.payload;
    },

    setSelectedCloser: (state, action: PayloadAction<number | null>) => {
      state.selectedCloser = action.payload;
    },

    setAppointmentDuration: (state, action: PayloadAction<number>) => {
      state.appointmentDuration = action.payload;
    },

    setAppointmentIsOnline: (state, action: PayloadAction<boolean>) => {
      state.appointmentIsOnline = action.payload;
    },

    setAppointmentMeetingLink: (state, action: PayloadAction<string>) => {
      state.appointmentMeetingLink = action.payload;
    },

    setAppointmentLocation: (state, action: PayloadAction<string>) => {
      state.appointmentLocation = action.payload;
    },

    setAppointmentNotes: (state, action: PayloadAction<string>) => {
      state.appointmentNotes = action.payload;
    },

    // Submission state
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    // Validation errors
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },

    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },

    clearAllErrors: (state) => {
      state.errors = {};
    },

    // Contact
    setSelectedContact: (state, action: PayloadAction<number | null>) => {
      state.selectedContactId = action.payload;
      // Reset new contact mode when selecting existing contact
      if (action.payload !== null) {
        state.isCreatingNewContact = false;
        state.newContactData = null;
      }
    },

    setContactSyncMode: (state, action: PayloadAction<'none' | 'add' | 'update'>) => {
      state.contactSyncMode = action.payload;
    },

    setIsCreatingNewContact: (state, action: PayloadAction<boolean>) => {
      state.isCreatingNewContact = action.payload;
      if (action.payload) {
        // Clear selected contact when entering new contact mode
        state.selectedContactId = null;
        // Default sync mode to 'add' when creating new contact
        state.contactSyncMode = 'add';
      } else {
        state.newContactData = null;
      }
    },

    setNewContactData: (state, action: PayloadAction<{ firstname?: string; lastname?: string } | null>) => {
      if (action.payload) {
        state.newContactData = {
          firstname: action.payload.firstname || '',
          lastname: action.payload.lastname || '',
        };
      } else {
        state.newContactData = null;
      }
    },
  },
});

// Export actions
export const {
  openForm,
  closeForm,
  resetForm,
  setStep,
  nextStep,
  prevStep,
  setCustomerType,
  setGender,
  setCustomerData,
  setBankData,
  setIsNewOccupant,
  setAssignedUserId,
  toggleEnergyType,
  setEnergyTypeData,
  setTariffsLoading,
  setAvailableTariffs,
  selectTariff,
  setIncludeContract,
  setContractLoading,
  setContractTemplate,
  updateContractField,
  updateFieldVisibility,
  setAvailableTemplates,
  setSelectedTemplate,
  setContractFieldValue,
  setScheduleAppointment,
  setAppointmentDate,
  setCloseMyself,
  setSelectedCloser,
  setAppointmentDuration,
  setAppointmentIsOnline,
  setAppointmentMeetingLink,
  setAppointmentLocation,
  setAppointmentNotes,
  setSubmitting,
  setErrors,
  clearError,
  clearAllErrors,
  setSelectedContact,
  setContactSyncMode,
  setIsCreatingNewContact,
  setNewContactData,
} = stOfferFormSlice.actions;

// Selectors
export const selectSTOfferForm = (state: { stOfferForm: STOfferFormState }) => state.stOfferForm;
export const selectIsOpen = (state: { stOfferForm: STOfferFormState }) => state.stOfferForm.isOpen;
export const selectCurrentStep = (state: { stOfferForm: STOfferFormState }) => state.stOfferForm.currentStep;

export default stOfferFormSlice.reducer;
